/**
 * PhoneGripDetector — infer "holding a phone" pose from hand landmarks.
 *
 * Used as a visual hint for the recording demo: when a phone is held below
 * or behind the exam paper the camera can't see the phone itself, but the
 * holding hand takes a characteristic partial-curl grip. This detector
 * flags that grip and exposes the center point (where the phone would be)
 * so the gaze overlay can redirect toward it. Observation only.
 *
 * Heuristic (from MediaPipe HandLandmarker 21-point topology):
 *   - Hand size    = ‖wrist(0) ↔ middle-MCP(9)‖
 *   - Finger curl  = ‖tip ↔ same-finger MCP‖ / handSize
 *                     · fully extended → ~1.5-2.0
 *                     · fully closed fist → ~0.4-0.5
 *                     · phone grip → ~0.65-1.35 (fingers wrap the device)
 *   - Thumb reach  = ‖thumb-tip(4) ↔ index-MCP(5)‖ / handSize
 *                     · tucked → ~0.3
 *                     · extended (grip side wall) → ≥0.55
 *
 * Both conditions must hold for a grip. A confidence score is computed
 * from how close the average finger curl sits to the canonical grip
 * value (0.95); consumers can threshold this or use it for UI opacity.
 */

export interface HandLandmark {
  x: number;
  y: number;
  z?: number;
}

export interface PhoneGripResult {
  isGrip: boolean;
  /** Image-normalized [0,1] center of the grip (avg of finger + thumb tips). */
  center: { x: number; y: number } | null;
  /** 0..1 — peaks at the canonical grip curl value. */
  confidence: number;
}

const GRIP_CURL_MIN = 0.65;
const GRIP_CURL_MAX = 1.35;
const GRIP_CURL_IDEAL = 0.95;
// Bumped 0.55 → 0.70 so cylindrical objects (red stick gripped like a mic)
// don't satisfy the thumb-extension criterion. Real phone holds keep thumb
// on the opposite side of the device → thumb tip far from index MCP.
const THUMB_EXT_MIN = 0.70;
// If thumb is pinching with index or middle (tip distance < this), the
// hand is in a writing/pinching posture, not a phone grip. Hard reject.
const PINCH_PRESENT_MAX = 0.55;

export function detectPhoneGrip(
  landmarks: HandLandmark[] | null | undefined,
): PhoneGripResult {
  if (!landmarks || landmarks.length < 21) {
    return { isGrip: false, center: null, confidence: 0 };
  }

  const wrist = landmarks[0];
  const middleMcp = landmarks[9];
  const handSize = Math.hypot(middleMcp.x - wrist.x, middleMcp.y - wrist.y);
  if (handSize < 0.015) {
    return { isGrip: false, center: null, confidence: 0 };
  }

  const curl = (tipIdx: number, mcpIdx: number): number => {
    const t = landmarks[tipIdx];
    const m = landmarks[mcpIdx];
    return Math.hypot(t.x - m.x, t.y - m.y) / handSize;
  };

  const indexCurl  = curl(8, 5);
  const middleCurl = curl(12, 9);
  const ringCurl   = curl(16, 13);
  const pinkyCurl  = curl(20, 17);
  const avgCurl = (indexCurl + middleCurl + ringCurl + pinkyCurl) / 4;

  const thumbTip = landmarks[4];
  const indexMcp = landmarks[5];
  const thumbExt =
    Math.hypot(thumbTip.x - indexMcp.x, thumbTip.y - indexMcp.y) / handSize;

  // Pinch guard — if thumb is touching/near index or middle tip, hand is
  // pinching (writing posture), not gripping a phone. Hard reject.
  const indexTip = landmarks[8];
  const middleTip = landmarks[12];
  const thumbIndexDist =
    Math.hypot(thumbTip.x - indexTip.x,  thumbTip.y - indexTip.y)  / handSize;
  const thumbMiddleDist =
    Math.hypot(thumbTip.x - middleTip.x, thumbTip.y - middleTip.y) / handSize;
  const isPinching =
    thumbIndexDist < PINCH_PRESENT_MAX || thumbMiddleDist < PINCH_PRESENT_MAX;

  const curlOk = avgCurl >= GRIP_CURL_MIN && avgCurl <= GRIP_CURL_MAX;
  const thumbOk = thumbExt >= THUMB_EXT_MIN;
  const isGrip = curlOk && thumbOk && !isPinching;

  // Center of the phone being held — average of the 5 fingertip positions.
  const cx =
    (landmarks[4].x + landmarks[8].x + landmarks[12].x + landmarks[16].x + landmarks[20].x) / 5;
  const cy =
    (landmarks[4].y + landmarks[8].y + landmarks[12].y + landmarks[16].y + landmarks[20].y) / 5;

  const confidence = isGrip
    ? Math.max(0, 1 - Math.abs(avgCurl - GRIP_CURL_IDEAL) / 0.4)
    : 0;

  return { isGrip, center: { x: cx, y: cy }, confidence };
}

// ──────────────────────────────────────────────────────────────────
// Pen-grip detection — distinguish from phone-grip.
//
// A pen grip differs in two key ways:
//   - Thumb-index-middle tips converge (pinch) — distance < 0.4·handSize
//   - Ring + pinky stay curled, index/middle stay semi-extended
//   - Total grip span (max fingertip pairwise distance) is small
// ──────────────────────────────────────────────────────────────────

export interface PenGripResult {
  isGrip: boolean;
  /** Image-normalized [0,1] center of the pinch (avg of thumb/index/middle tips). */
  center: { x: number; y: number } | null;
  /** 0..1 — based on how tightly the three tips converge. */
  confidence: number;
}

// Pen grip is just "pinch present + hand NOT fully open palm". The earlier
// tripod-only criterion (index extended along pen shaft) excluded the
// fist-wrap grip that's actually used when writing in a notebook for a
// while (2026-05-22 video review: user writing on paper read pen-grip = no
// because index was curled around pen, not extended).
// Pen grip = (any of multiple positive signals) AND (not open palm).
// Pure-geometry detection is inherently lossy because pen + finger
// orientation varies per writer. We trigger on ANY of three signals
// commonly seen in pen-holding hands; misfires get filtered by the
// pen+phone cross-check at the bimanual layer.
const PINCH_DIST_MAX  = 0.90;  // thumb↔index/middle tip distance / handSize
const TIGHT_PINCH_MAX = 0.55;  // strong pinch alone is sufficient
const FIST_CURL_MAX   = 0.85;  // closed-hand cue (writing-wrap grip)
const HAND_OPEN_MIN   = 1.25;  // avg curl above this = open palm, reject

export function detectPenGrip(
  landmarks: HandLandmark[] | null | undefined,
): PenGripResult {
  if (!landmarks || landmarks.length < 21) {
    return { isGrip: false, center: null, confidence: 0 };
  }

  const wrist = landmarks[0];
  const middleMcp = landmarks[9];
  const handSize = Math.hypot(middleMcp.x - wrist.x, middleMcp.y - wrist.y);
  if (handSize < 0.015) {
    return { isGrip: false, center: null, confidence: 0 };
  }

  const thumbTip = landmarks[4];
  const indexTip = landmarks[8];
  const middleTip = landmarks[12];

  const thumbIndex = Math.hypot(thumbTip.x - indexTip.x,  thumbTip.y - indexTip.y) / handSize;
  const thumbMiddle = Math.hypot(thumbTip.x - middleTip.x, thumbTip.y - middleTip.y) / handSize;
  const minPinch = Math.min(thumbIndex, thumbMiddle);

  const curl = (tipIdx: number, mcpIdx: number): number => {
    const t = landmarks[tipIdx];
    const m = landmarks[mcpIdx];
    return Math.hypot(t.x - m.x, t.y - m.y) / handSize;
  };
  const avgCurl = (curl(8, 5) + curl(12, 9) + curl(16, 13) + curl(20, 17)) / 4;

  // Reject open palm outright.
  if (avgCurl >= HAND_OPEN_MIN) {
    return { isGrip: false, center: null, confidence: 0 };
  }

  // Three OR'd positive signals — any one triggers pen grip:
  //   1) Tight pinch (clearly holding something thin)
  //   2) Moderate pinch (typical tripod/quadropod grip)
  //   3) Fist-wrap (closed hand, common after writing for a while)
  const tightPinch  = minPinch < TIGHT_PINCH_MAX;
  const modPinch    = minPinch < PINCH_DIST_MAX;
  const fistWrap    = avgCurl < FIST_CURL_MAX;
  const isGrip = tightPinch || modPinch || fistWrap;

  const cx = (thumbTip.x + indexTip.x + middleTip.x) / 3;
  const cy = (thumbTip.y + indexTip.y + middleTip.y) / 3;

  // Confidence: tighter pinch + closer-to-fist gets higher score.
  const pinchScore = Math.max(0, 1 - minPinch / PINCH_DIST_MAX);
  const shapeScore = Math.max(0, Math.min(1, (HAND_OPEN_MIN - avgCurl) / 0.5));
  const confidence = isGrip ? Math.min(1, 0.35 + 0.55 * Math.max(pinchScore, shapeScore)) : 0;

  return { isGrip, center: { x: cx, y: cy }, confidence };
}
